import find from 'lodash/find';
import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import sortBy from 'lodash/sortBy';
import { createSelector } from 'reselect';

import { BuiltinEndpointTypes } from 'xm-constants/authenticationTypes';
import { getBlockByIdMap, getBlocks } from 'selectors/flowDesigner';
import { getCustomSteps } from 'selectors/flowDesigner/utils/getCustomSteps';
import { FORM_NAME } from 'pages/EndpointsModal/constants';

const { XMATTERS } = BuiltinEndpointTypes;
const DEFAULT_ENDPOINTS = [];

const getAllEndpoints = state =>
  state.endpointsModal.endpoints ?? DEFAULT_ENDPOINTS;

export const getXmattersEndpoint = createSelector(
  [getAllEndpoints],
  endpoints => find(endpoints, { endpointType: XMATTERS }),
);

const getAuthTypeFilter = state => state.endpointsModal.authTypeFilter;

// If we open the EndpointsModal from a step configuration screen (for example, Slack - Create Channel)
// we only show the relevant endpoint types (in this case, endpoints with OAUTH_SLACK_v2 authentication type).
const getFilteredEndpoints = createSelector(
  [getAllEndpoints, getAuthTypeFilter],
  (endpoints, authTypeFilter) =>
    authTypeFilter
      ? endpoints.filter(
          endpoint => endpoint.authenticationType === authTypeFilter,
        )
      : endpoints,
);

const getIsNew = state => state.endpointsModal.isNew;
const getForm = state => state.form; // <= Redux Forms

// Naming left as was in refactor, this is specifically the endpoints to show in the modal:
export const getEndpoints = createSelector(
  [getFilteredEndpoints, getIsNew, getForm],
  (filteredEndpoints, isNew, form) =>
    isNew && form[FORM_NAME].values
      ? [form[FORM_NAME].values, ...filteredEndpoints]
      : filteredEndpoints,
);

export const getSelectedEndpoint = state =>
  get(state, 'endpointsModal.selectedEndpointOriginalValue', null);

export const getEndpointById = id => state => {
  const endpointsArray = get(state, ['endpointsModal', 'endpoints'], []);
  const specificEndpoint = endpointsArray.find(endpt => endpt.id === id);
  return specificEndpoint;
};

export const sortEndpoints = unsortedEndpointsArray => {
  const sorted = sortBy(unsortedEndpointsArray, ['name']);
  return sorted.sort((a, b) => {
    if (a.endpointType === XMATTERS) return -1;
    if (b.endpointType === XMATTERS) return 1;
    return 0;
  });
};

const getCurrentCanvas = state => get(state, 'flowDesigner.planCanvas');

export const getCustomStepByIdMap = createSelector([getCustomSteps], steps =>
  Object.freeze(keyBy(steps, 'id')),
);

export const getEndpointUsage = id => {
  const getEndpoint =
    id === undefined ? getSelectedEndpoint : getEndpointById(id);

  return createSelector(
    [
      getEndpoint,
      getBlocks,
      getBlockByIdMap,
      getCurrentCanvas,
      getCustomStepByIdMap,
    ],
    (selected, blocks = [], blockMap, canvas, stepMap) => {
      if (!selected) {
        return null;
      }

      if (!canvas || blocks.length === 0) {
        return selected.usages;
      }

      // filter out all usage by the current canvas and then recompute the usage
      const usagesByOthers = (selected.usages || []).filter(
        u => u.canvas.id !== canvas.id,
      );

      const seenBlocks = new Set();
      return blocks
        .filter(b => get(b, 'endpoint.id') === selected.id)
        .reduce((acc, b) => {
          if (!seenBlocks.has(b.id)) {
            seenBlocks.add(b.id);
            // Webhook from IB will break this.
            const block = blockMap[b.id];
            if (!block) return acc;

            const stepDef = block.step ? stepMap[block.step.id] : null;
            const step = {
              type: block.step,
              icon: stepDef ? stepDef.icon : null,
            };
            const usage = {
              block: { ...b, step },
              canvas,
            };
            acc.push(usage);
          }
          return acc;
        }, usagesByOthers);
    },
  );
};

export const getEndpointsModalIsOpen = state => !!state.endpointsModal.show;
